/* eslint-disable wrap-iife */
/*global ApplePaySession*/
/* eslint-disable no-console */
import store from 'store/Store';
import watch from 'redux-watch';
import addToBasketActions from 'actions/AddToBasketActions';
import checkoutUtils from 'utils/Checkout';
import helperUtils from 'utils/Helpers';
import localeUtils from 'utils/LanguageLocale';
import basketUtils from 'utils/Basket';
import userUtils from 'utils/User';
import urlUtils from 'utils/Url';
import orderUtils from 'utils/Order';
import Location from 'utils/Location';
import uIUtils from 'utils/UI';
import utilityApi from 'services/api/utility';
import checkoutApi from 'services/api/checkout';
import Actions from 'Actions';
import Flush from 'utils/localStorage/Flush';
import UtilActions from 'utils/redux/Actions';
import OrderActions from 'actions/OrderActions';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import cookieUtils from 'utils/Cookies';

const { getErrorStatesFromSubmitOrderAPI } = FrictionlessUtils;

const ApplePayCountry = {
    CA: localeUtils.COUNTRIES.CA.toLowerCase(),
    US: localeUtils.COUNTRIES.US.toLowerCase()
};

const TYPES = {
    ENABLED: 'ENABLED',
    DISABLED: 'DISABLED',
    HIDDEN: 'HIDDEN'
};

const CCARD_TYPES = ['amex', 'visa', 'masterCard', 'privateLabel'];

const CCARD_TYPES_US = ['discover'].concat(CCARD_TYPES);

const AMOUNT_TYPE = {
    FINAL: 'final',
    PENDING: 'pending'
};

const SESSION_STATES = {
    INITIATED: 'initiated',
    STARTED: 'started',
    TERMINATED: 'terminated'
};

const PREFIX = 'cc|';

const CURRENT_VERSION = 14;

const ERROR_CODES = { INVALID_ADDRESS_ERROR: -10170 };
const SESSION_ABORT_TIMEOUT = 2000;
const SESSION_SUCCESS_TIMEOUT = 2000;

export default (function () {
    const sessionStateKey = Symbol('session_state');
    let orderDetails;
    let shippingMethods;
    let mergeBasketError;
    let session;
    let currentShippingMethodId;
    let savedAddress;
    let isGuestCheckoutFlow = false;
    const getApplePayText = localeUtils.getLocaleResourceFile('services/ApplePay/locales', 'ApplePay');

    function isNewChaseFlow() {
        const chaseMigrationEnabled = Sephora.configurationSettings.paymentService?.chaseCommerce?.rampUp?.globalChaseCommerceMigrationEnabled;
        const isRCPSAppleChaseEnabled = cookieUtils.isRCPSAppleChaseEnabled();

        Sephora.logger.info('ApplePay Chase', { chaseMigrationEnabled, isRCPSAppleChaseEnabled });

        return chaseMigrationEnabled && isRCPSAppleChaseEnabled;
    }

    const merchantIdentifier = isNewChaseFlow()
        ? Sephora.configurationSettings.applePayChaseCommerceMerchantIdentifier
        : Sephora.configurationSettings.applePayMerchantIdentifier;

    function getSupportedApplePayVersion() {
        if (!window.ApplePaySession) {
            return null;
        }

        for (let version = CURRENT_VERSION; version >= 1; version--) {
            if (ApplePaySession.supportsVersion(version)) {
                return version;
            }
        }

        return null;
    }

    const errs = {
        invalidPostal: msg => new window.ApplePayError('shippingContactInvalid', 'postalCode', getApplePayText('invalidPostal') || msg),
        invalidPostalBilling: msg => new window.ApplePayError('billingContactInvalid', 'postalCode', getApplePayText('invalidBillingPostal') || msg),
        invalidPhone: msg => new window.ApplePayError('shippingContactInvalid', 'phoneNumber', getApplePayText('invalidPhone') || msg),
        invalidCity: msg =>
            new window.ApplePayError(
                'shippingContactInvalid',
                'locality', // Apple Pay field for city
                getApplePayText('invalidCity') || msg
            ),
        invalidState: msg =>
            new window.ApplePayError(
                'shippingContactInvalid',
                'administrativeArea', // Apple Pay field for state
                getApplePayText('invalidState') || msg
            ),
        invalidAddress: msg => new window.ApplePayError('shippingContactInvalid', 'postalAddress', getApplePayText('invalidAddress') || msg),
        unserviceable: msg => new window.ApplePayError('addressUnserviceable', 'postalAddress', getApplePayText('unserviceable') || msg),
        hazmatRestricted: msg => new window.ApplePayError('addressUnserviceable', 'postalAddress', getApplePayText('hazmatRestricted') || msg)
    };

    function isBopisOrder() {
        return orderDetails?.header?.isBopisOrder || !basketUtils.isDCBasket();
    }

    function startSession(currentSession) {
        Sephora.logger.info('Starting ApplePay session', { currentSession, sessionStateKey });

        if (currentSession[sessionStateKey] !== SESSION_STATES.STARTED) {
            currentSession.begin();
            currentSession[sessionStateKey] = SESSION_STATES.STARTED;
        }
    }

    function abortSession(currentSession) {
        Sephora.logger.info('Aborting ApplePay session', { currentSession, sessionStateKey });

        if (currentSession[sessionStateKey] === SESSION_STATES.STARTED) {
            currentSession.abort();
            currentSession[sessionStateKey] = SESSION_STATES.TERMINATED;
        }
    }

    function getOrderTotal() {
        const basketData = store.getState().basket;
        const basket = isBopisOrder() ? basketData.pickupBasket : basketData;
        const amount = basketUtils.removeCurrency(orderDetails ? orderDetails.priceInfo.orderTotal || '' : basket.subtotal || '');

        return {
            label: 'Estimated Total',
            amount: amount,
            type: AMOUNT_TYPE.FINAL
        };
    }

    function getShippingMethods() {
        // Define shipping method type strings for filtering
        const unscheduledShippingMethodTypeEnKey = 'same day delivery';
        const unscheduledShippingMethodTypeFrKey = 'livraison le jour même';
        const unscheduledFreeShippingMethodTypeEnKey = 'free rouge same day delivery';
        const scheduledShippingMethodTypeEnKey = 'schedule a delivery window';
        const scheduledShippingMethodTypeFrKey = 'prévoir la livraison';
        const scheduledShippingMethodTypeRougeFrKey = 'planifier une fenêtre de livraison';

        let seenSameDay = false;

        const methods = (shippingMethods || [])
            .filter(method => {
                const type = method.shippingMethodType?.toLowerCase() || '';
                const description = method.shippingMethodDescription?.toLowerCase() || '';

                // Remove "Schedule Delivery Window" options using defined keys
                if (
                    type === scheduledShippingMethodTypeEnKey ||
                    type === scheduledShippingMethodTypeFrKey ||
                    type === scheduledShippingMethodTypeRougeFrKey ||
                    description.includes(scheduledShippingMethodTypeEnKey) ||
                    description.includes(scheduledShippingMethodTypeFrKey) ||
                    description.includes(scheduledShippingMethodTypeRougeFrKey)
                ) {
                    return false;
                }

                // Remove duplicate Same Day Delivery options using defined keys
                if (
                    type === unscheduledShippingMethodTypeEnKey ||
                    type === unscheduledShippingMethodTypeFrKey ||
                    type === unscheduledFreeShippingMethodTypeEnKey
                ) {
                    if (seenSameDay) {
                        return false;
                    }

                    seenSameDay = true;
                }

                return true;
            })
            .map(function (group) {
                let description = group.shippingMethodDescription;

                // We need to cut "Delivery" part to win some space on ApplePay sheet
                // per https://jira.sephora.com/browse/ILLUPH-87511
                const cutIndex = description.toLowerCase().indexOf('days');

                if (cutIndex !== -1) {
                    description = description.substring(0, cutIndex + 4) + ')';
                }

                return {
                    label: group.shippingMethodType,
                    detail: description,
                    amount: basketUtils.removeCurrency(group.shippingFee || ''),
                    identifier: group.shippingMethodId
                };
            });

        /**
         * If Current Shipping Method is not the first in the list of methods,
         * Then we need to put it in the first place -
         * ApplePay always shows the first one as selected.
         */
        const methodId = methods.map(method => Number(method.identifier)).indexOf(currentShippingMethodId);

        if (methodId > 0) {
            const methodToShift = methods.splice(methodId, 1);
            methods.unshift(methodToShift[0]);
        }

        return methods;
    }

    function getShippingGroupFromOrder() {
        /* eslint indent: 0 */
        const sameDayDeliveryBasket = (orderDetails?.items?.itemsByBasket || []).find(
            obj => obj.basketType === addToBasketActions.BASKET_TYPES.SAMEDAY_BASKET
        );

        return orderDetails?.shippingGroups?.shippingGroupsEntries
            ? orderDetails.shippingGroups.shippingGroupsEntries.filter(
                  item =>
                      item.shippingGroupType ===
                      (sameDayDeliveryBasket ? OrderActions.SHIPPING_GROUPS.SAME_DAY : OrderActions.SHIPPING_GROUPS.HARDGOOD)
              )[0].shippingGroup
            : {
                  shippingGroupId: 0,
                  shippingMethod: { shippingMethodId: 0 }
              };
    }

    function getShippingMethodErrorMessage() {
        const shippingMethod = getShippingGroupFromOrder().shippingMethod;
        const shippingFee = checkoutUtils.setShippingFee(shippingMethod.shippingFee);
        const getText = localeUtils.getLocaleResourceFile('utils/locales', 'Checkout');

        return getText('shippingMethodError', [shippingMethod.shippingMethodType + ' (' + shippingFee + ')']);
    }

    function getLineItems() {
        const { getProp } = helperUtils;
        const { removeCurrency } = basketUtils;

        const lineItems = [];

        lineItems.push({
            label: 'Merchandise Subtotal',
            amount: removeCurrency(getProp(orderDetails, 'priceInfo.merchandiseSubtotal', ''))
        });

        if (getProp(orderDetails, 'priceInfo.bagFeeSubtotal', false)) {
            lineItems.push({
                label: 'Bag Fees',
                amount: removeCurrency(getProp(orderDetails, 'priceInfo.bagFeeSubtotal', ''))
            });
        }

        if (getProp(orderDetails, 'priceInfo.pif', false)) {
            lineItems.push({
                label: 'Special Fees',
                amount: removeCurrency(getProp(orderDetails, 'priceInfo.pif', ''))
            });
        }

        lineItems.push({
            label: 'Estimated Tax',
            amount: removeCurrency(getProp(orderDetails, 'priceInfo.tax', ''))
        });

        if (!isBopisOrder()) {
            lineItems.push({
                label: 'Shipping',
                amount: removeCurrency(getProp(orderDetails, 'priceInfo.totalShipping', ''))
            });
        }

        if (getProp(orderDetails, 'priceInfo.promotionDiscount')) {
            lineItems.push({
                label: 'Discounts',
                amount: '-' + removeCurrency(getProp(orderDetails, 'priceInfo.promotionDiscount', ''))
            });
        }

        if (getProp(orderDetails, 'priceInfo.storeCardAmount')) {
            lineItems.push({
                label: 'Account Credit',
                amount: '-' + removeCurrency(getProp(orderDetails, 'priceInfo.storeCardAmount', ''))
            });
        }

        return lineItems;
    }

    function formatAddress(appleAddress, isBilling) {
        const addressLines = appleAddress.addressLines || [];
        const phoneNumber = appleAddress.phoneNumber || '1234567890';
        const address = {
            address1: addressLines.join(' ') || '',
            address2: '',
            city: appleAddress.locality ? appleAddress.locality.toUpperCase() : '',
            state: appleAddress.administrativeArea,
            postalCode: (appleAddress.postalCode || '').replace(/\n/gm, '').trim(),
            country: appleAddress.countryCode ? appleAddress.countryCode.toUpperCase() : '',
            phone: phoneNumber.replace(helperUtils.specialCharacterRegex, '').slice(0, 10)
        };
        const firstName = appleAddress.givenName || '';
        const lastName = appleAddress.familyName || '';

        if (!isBilling) {
            address.firstName = firstName;
            address.lastName = lastName;
        }

        if (isGuestCheckoutFlow) {
            address.shipEmail = Location.isCheckout() ? orderUtils.getGuestEmail(orderDetails) : appleAddress.emailAddress;
        }

        return isBilling
            ? {
                  firstName: firstName,
                  lastName: lastName,
                  address: address
              }
            : address;
    }

    function formatCreditCard(payment) {
        let paymentData = btoa(JSON.stringify(payment.token.paymentData));

        if (isNewChaseFlow()) {
            paymentData = PREFIX + paymentData;
        }

        Sephora.logger.info('ApplePay Chase formatCreditCard', { paymentData, payment });

        return {
            paymentData,
            paymentNetwork: payment.token.paymentMethod.network,
            paymentDisplayInfo: payment.token.paymentMethod.displayName,
            creditCard: formatAddress(payment.billingContact, true)
        };
    }

    function getShippingGroupIdFromOrder() {
        return getShippingGroupFromOrder().shippingGroupId;
    }

    function getShippingMethodIdFromOrder() {
        return Number(getShippingGroupFromOrder().shippingMethod.shippingMethodId);
    }

    async function fillAddress(address) {
        const zipCode = (address.postalCode || '').trim();

        // API does not give a full postal code for Canada during
        // shippingContactSelected event, so we just append a dummy value to
        // pass validation
        if ((address.countryCode || '').toLowerCase() === ApplePayCountry.CA) {
            address.postalCode = zipCode.length === 3 ? zipCode + '1A1' : zipCode;

            return address;
        }

        // API does not give city/state for US addresses during shippingContactSelected event,
        // but we can get them from zip code
        const data = await utilityApi.getStateAndCityForZipCode(address.countryCode, zipCode);
        address.administrativeArea = data.state;
        address.locality = data.city;

        return address;
    }

    async function preparePaymentData(payment) {
        if (!payment) {
            Sephora.logger.info('preparePaymentData called without payment');
            throw new Error('Missing payment object');
        }

        const { shippingContact, billingContact } = payment;

        if (!shippingContact || !billingContact) {
            Sephora.logger.info('preparePaymentData missing contact data', { shippingContact, billingContact });
            throw new Error('Missing shipping or billing contact');
        }

        // since billing contact from Apple doesn't have it's own phone
        payment.billingContact.phoneNumber = payment.shippingContact.phoneNumber;

        if (isBopisOrder()) {
            await fillAddress(payment.billingContact);
        } else {
            await fillAddress(payment.shippingContact);
            await fillAddress(payment.billingContact);
        }
    }

    function createApplePayPaymentRequest() {
        const country = localeUtils.getCurrentCountry();
        const currency = localeUtils.isCanada() ? 'CAD' : 'USD';
        const supportedCards = localeUtils.isUS() ? CCARD_TYPES_US : CCARD_TYPES;
        const shippingFields =
            isGuestCheckoutFlow && !Location.isCheckout()
                ? ['postalAddress', 'name', 'phone', 'email']
                : isBopisOrder()
                  ? ['phone']
                  : ['postalAddress', 'name', 'phone'];

        Sephora.logger.info('Creating ApplePay payment request', shippingFields);

        return {
            countryCode: country,
            currencyCode: currency,
            supportedNetworks: supportedCards,
            merchantCapabilities: ['supports3DS'],
            requiredShippingContactFields: shippingFields,
            requiredBillingContactFields: ['postalAddress', 'name'],
            lineItems: [],
            total: getOrderTotal()
        };
    }

    function validateApplePayMerchant(validationUrl) {
        let domainName = Location.getLocation().host;

        if (isNewChaseFlow()) {
            domainName = PREFIX + domainName;
        }

        Sephora.logger.info('ApplePay merchant validation', { domainName, validationUrl });

        return checkoutApi
            .validateApplePayMerchant(domainName, validationUrl)
            .then(function (data) {
                Sephora.logger.info('ApplePay merchant validation succeeded', { data });
                session.completeMerchantValidation(data);
            })
            .catch(function (reason) {
                Sephora.logger.info('ApplePay merchant validation failed', { reason });
                abortSession(session);
            });
    }

    /**
     * The idea of this function is:
     * - to abort the ApplePay sheet after SESSION_ABORT_TIMEOUT secs
     * - and Show sticky ApplePay button instead of Checkout button,
     *      so ApplePay sheet will be accessible for user in one click
     * @param forceClose
     */
    function handleBasketError(forceClose) {
        const basketData = store.getState().basket;
        const basket = isBopisOrder() ? basketData.pickupBasket : basketData;

        if (!basket.showStickyApplePayBtn || forceClose) {
            store.dispatch(addToBasketActions.showStickyApplePayBtn(true));
            setTimeout(() => {
                abortSession(session);
                store.dispatch(Actions.showInterstice(false));
                uIUtils.unlockBackgroundPosition();
            }, SESSION_ABORT_TIMEOUT);
        }

        uIUtils.scrollToTop();
    }

    function handleShippingMethodError(payment) {
        Sephora.logger.error('ApplePay shipping method error', { payment });

        store.dispatch(
            addToBasketActions.showError({
                internalError: getShippingMethodErrorMessage()
            })
        );

        savedAddress = Object.assign({}, payment.shippingContact);
        handleBasketError(true);

        session.completePayment(ApplePaySession.STATUS_FAILURE, [errs.unserviceable(getShippingMethodErrorMessage())]);
    }

    function handleFrictionlessErrors(reason) {
        Sephora.logger.error('ApplePay handleFrictionlessErrors', { reason });
        const errors = getErrorStatesFromSubmitOrderAPI(reason);

        if (Object.keys(errors || {}).length > 0) {
            FrictionlessCheckoutBindings.setPlaceOrderBtnClickanalytics({
                isSuccess: false,
                hasErrorMessage: reason?.errorMessages?.length > 0
            });
            uIUtils.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });
            store.dispatch(OrderActions.setCheckoutSectionErrors(errors));
        }
    }

    function handlePlaceOrderError(reason) {
        Sephora.logger.error('ApplePay placeOrder failed', { reason });
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);

        setTimeout(() => {
            abortSession(session);
        }, SESSION_ABORT_TIMEOUT);

        if (Location.isFrictionlessCheckoutPage()) {
            handleFrictionlessErrors(reason);
        }

        if (reason.errorMessages) {
            store.dispatch(addToBasketActions.showError({ internalError: reason.errorMessages.join(' ') }));
            store.dispatch(addToBasketActions.refreshBasket());
            handleBasketError(true);
        }
    }

    function handleCreateShippingAddressError(reason) {
        Sephora.logger.error('ApplePay handleCreateShippingAddressError', { reason });
        const errsList = [];

        if (reason.errorCode === ERROR_CODES.INVALID_ADDRESS_ERROR && reason?.errors?.phoneNumber) {
            errsList.push(errs.invalidPhone());
        } else {
            errsList.push(errs.invalidPostal());
        }

        if (reason.errorFields?.length > 0) {
            reason.errorFields.forEach(field => {
                if (field.includes('city')) {
                    errsList.push(errs.invalidCity());
                } else {
                    if (field.includes('state')) {
                        errsList.push(errs.invalidState());
                    } else {
                        if (field.includes('postal')) {
                            errsList.push(errs.invalidPostal());
                        }
                    }
                }
            });
        }

        session.completePayment(ApplePaySession.STATUS_FAILURE, errsList);
    }

    function handleInitializeCheckoutError(reason) {
        Sephora.logger.error('ApplePay handleInitializeCheckoutError', { reason });
        store.dispatch(
            addToBasketActions.showError({
                internalError: reason.errorMessages.join(' ')
            })
        );

        store.dispatch(addToBasketActions.refreshBasket());

        handleBasketError(true);
    }

    async function placeApplePayOrder() {
        try {
            const order = await checkoutApi.placeOrder({
                originOfOrder: 'mobileWeb',
                jscData: true
            });

            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);

            // Empty user's basket data after order is placed
            Flush.flushBasket();

            setTimeout(() => {
                store.dispatch(Actions.showInterstice(true));
                urlUtils.redirectTo('/checkout/confirmation?orderId=' + order.orderId);
            }, SESSION_SUCCESS_TIMEOUT);
        } catch (reason) {
            handlePlaceOrderError(reason);
        }
    }

    /**
     * Validates hazmat restrictions for final authorization.
     * For Canada, the whole postal code is not given to us until final authorization,
     * so we need to check the hazmat restriction here again.
     * @param {string} zipCode - Postal/zip code
     * @param {string} state - State/province
     * @returns {Promise<boolean>} - Returns true if validation passes, false if restricted
     */
    async function validateFinalHazmatRestrictions(zipCode, state) {
        if (!Sephora.configurationSettings.isApplePayHazmatItemsEnabled) {
            return true;
        }

        // Only check hazmat location restrictions if basket contains hazmat items
        if (!basketUtils.isHazardous()) {
            return true;
        }

        try {
            const data = await checkoutApi.checkHazmatLocation({ zipCode, state });
            Sephora.logger.info('ApplePay checkHazmatLocation', { data });

            if (data.isShippingRestricted) {
                session.completePayment(ApplePaySession.STATUS_FAILURE, [errs.hazmatRestricted()]);

                return false;
            }

            return true;
        } catch (error) {
            Sephora.logger.error('ApplePay checkHazmatLocation failed', { error });
            session.completePayment(ApplePaySession.STATUS_FAILURE, [errs.unserviceable()]);

            return false;
        }
    }

    /**
     * Initializes checkout for payment authorization.
     * @returns {Promise<boolean>} - Returns true if initialization succeeds and session is active
     */
    async function initializeCheckoutForPayment() {
        if (!isGuestCheckoutFlow) {
            await checkoutUtils.initializeCheckout({
                isApplePayFlow: true,
                ropisCheckout: isBopisOrder()
            });
        }

        if (session[sessionStateKey] === SESSION_STATES.TERMINATED) {
            startSession(session);

            return false;
        }

        return true;
    }

    /**
     * Processes shipping address for payment and validates shipping method hasn't changed.
     * @param {Object} payment - Apple Pay payment object
     * @returns {Promise<boolean>} - Returns true if processing succeeds and shipping method is valid
     */
    async function processShippingAddressForPayment(payment) {
        if (!isBopisOrder()) {
            const shippingAddressToProcess = {
                shippingGroupId: getShippingGroupIdFromOrder(),
                address: formatAddress(payment.shippingContact),
                saveToProfile: false
            };

            await checkoutApi.createShippingAddress(shippingAddressToProcess);
        }

        orderDetails = await checkoutApi.getOrderDetails(orderDetails.header.orderId);
        const newShippingMethodId = getShippingMethodIdFromOrder();

        if (currentShippingMethodId !== newShippingMethodId && !isBopisOrder()) {
            handleShippingMethodError(payment);

            return false;
        }

        return true;
    }

    /**
     * Processes payment and places the Apple Pay order.
     * @param {Object} payment - Apple Pay payment object
     * @returns {Promise<void>}
     */
    async function processPaymentAndPlaceOrder(payment) {
        savedAddress = null;
        const postData = formatCreditCard(payment);

        try {
            await checkoutApi.addCreditCardToOrder(postData, true);
            await placeApplePayOrder();
        } catch {
            session.completePayment(ApplePaySession.STATUS_FAILURE, [errs.invalidPostalBilling()]);
            throw new Error('Failed to add credit card or place order');
        }
    }

    function applePayPaymentMethodSelected() {
        session.completePaymentMethodSelection(getOrderTotal(), getLineItems());
    }

    function handleRestrictedShippingError(reason) {
        if (reason) {
            const errorMessage = reason.errors && (reason.errors.restrictedShipping || reason.errors.stateRestricted);

            if (errorMessage) {
                store.dispatch(addToBasketActions.showError({ internalError: errorMessage }));
                handleBasketError();
            }
        }
    }

    function handleInvalidZipCodeError(reason) {
        if (reason) {
            const errorMessage = reason?.errors?.invalidAddress;

            if (errorMessage) {
                store.dispatch(addToBasketActions.showError({ internalError: errorMessage }));
                handleBasketError();
            }
        }
    }

    function handleOutOfStockError(reason) {
        if (reason && reason.errors && (reason.errors.inventoryNotAvailable || reason.errors.sameDaySkuOOSException)) {
            store.dispatch(addToBasketActions.showError({ internalError: reason.errorMessages.join(' ') }));
            store.dispatch(addToBasketActions.refreshBasket());
            handleBasketError(true);
        }
    }

    function handleShippingContactCompletion(errors = []) {
        const update = {
            newShippingMethods: getShippingMethods(),
            newTotal: getOrderTotal(),
            newLineItems: getLineItems()
        };

        if (errors && errors.length) {
            update.errors = errors;
        }

        Sephora.logger.info('ApplePay handleShippingContactCompletion', { update });

        session.completeShippingContactSelection(update);
    }

    function handleMergeBasketError() {
        Sephora.logger.error('ApplePay applePayShippingContactSelected mergeBasketError', mergeBasketError);

        const errors = [];

        errors.push(errs.hazmatRestricted());

        handleBasketError();

        handleShippingContactCompletion(errors);
    }

    async function updateShippingAddress(shippingAddressToProcess) {
        try {
            Sephora.logger.info('ApplePay createShippingAddress (updateShippingAddress)', { shippingAddressToProcess });
            const shippingAddress = await checkoutApi.createShippingAddress(shippingAddressToProcess);
            Sephora.logger.info('ApplePay createShippingAddress (updateShippingAddress)', { shippingAddress });

            orderDetails = await checkoutApi.getOrderDetails(orderDetails.header.orderId);

            currentShippingMethodId = getShippingMethodIdFromOrder();
            const shippingGroupId = getShippingGroupIdFromOrder();

            const availableShippingMethodsFull = await checkoutApi.getAvailableShippingMethods(orderDetails.header.orderId, shippingGroupId);
            shippingMethods = availableShippingMethodsFull?.shippingMethods;

            Sephora.logger.info('ApplePay getAvailableShippingMethods (updateShippingAddress)', { shippingMethods });

            handleShippingContactCompletion();
        } catch (reason) {
            Sephora.logger.error('ApplePay createShippingAddress (updateShippingAddress) failed', { reason });

            handleRestrictedShippingError(reason);
            handleInvalidZipCodeError(reason);
            handleOutOfStockError(reason);

            const error = reason?.errorMessages?.join('. ');
            const appleErrors = [errs.unserviceable(error) || errs.invalidPostal()];

            handleShippingContactCompletion(appleErrors);
        }
    }

    function applePayShippingContactSelected(request, event) {
        Sephora.logger.info('ApplePay applePayShippingContactSelected', {
            request,
            event,
            mergeBasketError
        });

        // Show Shipping Section in Error state if basket is Hazardous
        if (mergeBasketError) {
            handleMergeBasketError();

            return request;
        }

        let shippingContact = event.shippingContact;

        if (savedAddress && savedAddress.locality === shippingContact.locality) {
            shippingContact = savedAddress;
        }

        const shippingAddressToProcess = {
            shippingGroupId: getShippingGroupIdFromOrder(),
            address: formatAddress(shippingContact, false),
            saveToProfile: false
        };

        Sephora.logger.info('ApplePay applePayShippingContactSelected shippingAddressToProcess', {
            shippingAddressToProcess
        });

        updateShippingAddress(shippingAddressToProcess);

        return request;
    }

    function applePayShippingMethodSelected(request, event) {
        const shippingMethodData = {
            shippingGroupId: getShippingGroupIdFromOrder(),
            shippingMethodId: event.shippingMethod.identifier,
            orderId: orderDetails.header.orderId
        };

        checkoutApi
            .setShippingMethod(shippingMethodData)
            .then(() => checkoutApi.getOrderDetails(orderDetails.header.orderId))
            .then(data => {
                orderDetails = data;
                currentShippingMethodId = getShippingMethodIdFromOrder();
                session.completeShippingMethodSelection(ApplePaySession.STATUS_SUCCESS, getOrderTotal(), getLineItems());
            })
            .catch(() => {
                session.completeShippingMethodSelection(ApplePaySession.STATUS_FAILURE, getOrderTotal(), getLineItems());
            });

        return request;
    }

    function handleShippingContact(request, event) {
        fillAddress(event.shippingContact)
            .then(enrichedContact => {
                const enrichedEvent = {
                    ...event,
                    shippingContact: enrichedContact
                };

                Sephora.logger.info('ApplePay handleShippingContact enrichedContact', {
                    enrichedContact
                });

                applePayShippingContactSelected(request, enrichedEvent);
            })
            .catch(error => {
                Sephora.logger.error('ApplePay fillAddress failed', { error });
                handleShippingContactCompletion([errs.invalidPostal()]);
            });
    }

    async function validateShippingAddress({ city, state, zipCode, country }) {
        if (Sephora.configurationSettings.isApplePayAddressValidation) {
            try {
                const validatedAddress = await utilityApi.taxServiceValidateAddress({
                    city,
                    state,
                    postalCode: zipCode,
                    country
                });

                Sephora.logger.info('ApplePay taxServiceValidateAddress', { validatedAddress });

                if (validatedAddress?.validLocation === false) {
                    return false;
                }

                return true;
            } catch (error) {
                Sephora.logger.error('ApplePay taxServiceValidateAddress failed', { error });

                return false;
            }
        } else {
            return true;
        }
    }

    async function checkHazmatLocation({ zipCode, state }) {
        if (!Sephora.configurationSettings.isApplePayHazmatItemsEnabled) {
            return true;
        }

        // Only check hazmat location restrictions if basket contains hazmat items
        if (!basketUtils.isHazardous()) {
            return true;
        }

        try {
            const data = await checkoutApi.checkHazmatLocation({ zipCode, state });
            Sephora.logger.info('ApplePay checkHazmatLocation', { data });

            if (data.isShippingRestricted) {
                handleShippingContactCompletion([errs.hazmatRestricted(data.errorMessage)]);

                return false;
            }

            return true;
        } catch (error) {
            Sephora.logger.error('ApplePay checkHazmatLocation failed', { error });
            handleShippingContactCompletion([errs.unserviceable()]);

            return false;
        }
    }

    async function shippingContactSelected(request, event) {
        const zipCode = event.shippingContact.postalCode;
        const state = event.shippingContact.administrativeArea;
        const city = event.shippingContact.locality;
        const country = event.shippingContact.countryCode;

        Sephora.logger.info('ApplePay shippingContactSelected', {
            request,
            event,
            zipCode,
            state,
            city,
            country
        });

        // At this point (before payment authorization) Apple just gives us a partial
        // postal code for Canadian addresses, so we skip validation here for Canada
        // and perform it again at paymentAuthorized
        if (country === localeUtils.COUNTRIES.US) {
            const isValidAddress = await validateShippingAddress({
                city,
                state,
                zipCode,
                country
            });

            if (!isValidAddress) {
                handleShippingContactCompletion([errs.invalidAddress()]);

                return;
            }
        }

        const canShipHazmat = await checkHazmatLocation({ zipCode, state });

        if (!canShipHazmat) {
            return;
        }

        handleShippingContact(request, event);
    }

    async function paymentAuthorized(request, event) {
        const payment = event.payment;
        const zipCode = payment?.shippingContact?.postalCode;
        const state = payment?.shippingContact?.administrativeArea;
        const country = payment?.shippingContact?.countryCode?.toUpperCase();
        const city = payment?.shippingContact?.locality;

        Sephora.logger.info('ApplePay payment authorized', {
            request,
            event,
            payment,
            zipCode,
            state
        });

        // Validate hazmat restrictions for final authorization
        const isHazmatValid = await validateFinalHazmatRestrictions(zipCode, state);

        if (!isHazmatValid) {
            return;
        }

        // This validation is done for US when the user changes the shipping address
        // but for Canada we get the full postal code only at this stage,
        // so we need to validate it here
        if (country === localeUtils.COUNTRIES.CA) {
            const isValidAddress = await validateShippingAddress({
                city,
                state,
                zipCode,
                country
            });

            if (!isValidAddress) {
                session.completePayment(ApplePaySession.STATUS_FAILURE, [errs.invalidAddress()]);

                return;
            }
        }

        // Initialize checkout
        try {
            const isCheckoutInitialized = await initializeCheckoutForPayment();

            if (!isCheckoutInitialized) {
                return;
            }
        } catch (reason) {
            handleInitializeCheckoutError(reason);

            return;
        }

        // Process shipping address and validate shipping method
        try {
            const isShippingValid = await processShippingAddressForPayment(payment);

            if (!isShippingValid) {
                return;
            }
        } catch (reason) {
            handleCreateShippingAddressError(reason);

            return;
        }

        // Process payment and place order
        try {
            await processPaymentAndPlaceOrder(payment);
        } catch (error) {
            // Error already handled in processPaymentAndPlaceOrder
            Sephora.logger.error('ApplePay processPaymentAndPlaceOrder failed', { error });
        }
    }

    function applePayPaymentAuthorized(request, event) {
        Sephora.logger.info('ApplePay applePayPaymentAuthorized authorized', { request, event });
        preparePaymentData(event.payment)
            .then(data => {
                Sephora.logger.info('ApplePay preparePaymentData succeeded', { data, event });
                paymentAuthorized(request, event);
            })
            .catch(error => {
                Sephora.logger.error('ApplePay preparePaymentData failed', { error });
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            });
    }

    function cancel(event) {
        Sephora.logger.info('ApplePay session cancelled', { event });
        store.dispatch(Actions.showInterstice(false));

        if (Location.isCheckout()) {
            store.dispatch(UtilActions.merge('order', 'isApplePayFlow', false));
        } else {
            store.dispatch(UtilActions.merge('applePaySession', 'isActive', false));
        }
    }

    function prepareSession() {
        // Reset the default values for the Session
        const request = createApplePayPaymentRequest();
        const version = getSupportedApplePayVersion();
        session = new window.ApplePaySession(version, request);
        Sephora.logger.info('ApplePay session created', { version, request });
        session[sessionStateKey] = SESSION_STATES.INITIATED;
        store.dispatch(UtilActions.merge('applePaySession', 'isActive', true));
        mergeBasketError = false;

        session.onvalidatemerchant = event => validateApplePayMerchant(event.validationURL);

        session.onpaymentmethodselected = event => applePayPaymentMethodSelected(request, event);

        session.onshippingcontactselected = event => shippingContactSelected(request, event);

        session.onshippingmethodselected = event => applePayShippingMethodSelected(request, event);

        session.onpaymentauthorized = event => applePayPaymentAuthorized(request, event);

        session.oncancel = event => cancel(event);
    }

    function enableGuestCheckout() {
        isGuestCheckoutFlow = true;
    }

    function onApplePayClicked() {
        prepareSession();

        checkoutUtils
            .initializeCheckout({
                isApplePayFlow: true,
                isGuestCheckout: isGuestCheckoutFlow,
                ropisCheckout: isBopisOrder()
            })
            .then(order => {
                checkoutApi.getOrderDetails(isGuestCheckoutFlow ? 'current' : order.orderId).then(data => {
                    orderDetails = data;
                    Sephora.logger.info('ApplePay session created', { data });
                    startSession(session);
                });
            })
            .catch(error => {
                Sephora.logger.info('ApplePay initializeCheckout failed', { error });
                checkoutUtils.initOrderFailure(error);
            });

        // If user became recognized - Abort current session
        const userWatch = watch(store.getState, 'user');
        store.subscribe(
            userWatch(() => {
                if (!userUtils.isSignedIn()) {
                    abortSession(session);
                }
            }),
            { ignoreAutoUnsubscribe: true }
        );
    }

    async function checkApplePayCapabilities() {
        try {
            const capabilities = await window.ApplePaySession.applePayCapabilities(merchantIdentifier);
            Sephora.logger.info('ApplePay capabilities', capabilities);

            switch (capabilities.paymentCredentialStatus) {
                case 'paymentCredentialsAvailable':
                case 'paymentCredentialStatusUnknown':
                    return TYPES.ENABLED;
                case 'paymentCredentialsUnavailable':
                default:
                    return TYPES.HIDDEN;
            }
        } catch {
            return TYPES.HIDDEN;
        }
    }

    async function canMakePayments() {
        try {
            const canMakeApplePayments = window.ApplePaySession.canMakePayments();
            Sephora.logger.info('ApplePay canMakePayments', canMakeApplePayments);

            return canMakeApplePayments ? TYPES.ENABLED : TYPES.HIDDEN;
        } catch {
            return TYPES.HIDDEN;
        }
    }

    async function canMakePaymentsWithActiveCard() {
        try {
            const hasActive = await window.ApplePaySession.canMakePaymentsWithActiveCard(merchantIdentifier);
            Sephora.logger.info('ApplePay canMakePaymentsWithActiveCard', hasActive);

            return hasActive ? TYPES.ENABLED : TYPES.HIDDEN;
        } catch {
            return TYPES.HIDDEN;
        }
    }

    async function checkApplePayments() {
        if (!window.ApplePaySession) {
            return TYPES.HIDDEN;
        }

        Sephora.logger.info('ApplePay merchantIdentifier', merchantIdentifier);

        if (typeof window.ApplePaySession.applePayCapabilities === 'function') {
            return await checkApplePayCapabilities();
        } else if (typeof window.ApplePaySession.canMakePayments === 'function') {
            return await canMakePayments();
        } else if (typeof window.ApplePaySession.canMakePaymentsWithActiveCard === 'function') {
            return await canMakePaymentsWithActiveCard();
        }

        return TYPES.HIDDEN;
    }

    async function getApplePaymentType(orderData) {
        const isApplePayHazmatEnabled = Sephora.configurationSettings.isApplePayHazmatItemsEnabled;
        const isCheckout = Location.isCheckout();
        let isApplePayEnabled;

        // The property to check if ApplePay is enabled is in different locations depending
        // if we are in checkout or not, and if isApplePayHazmatEnabled is enabled or not
        if (isApplePayHazmatEnabled) {
            isApplePayEnabled = isCheckout ? orderData.items.forceShowApplePayPopupForHazmat : orderData.forceShowApplePayPopupForHazmat;
        } else {
            isApplePayEnabled = isCheckout ? orderData.header.isApplePayEnabled : orderData.isApplePayEnabled;
        }

        if (orderData.isInitialized && isApplePayEnabled) {
            const type = await checkApplePayments();

            if (type !== TYPES.HIDDEN) {
                return orderData.items.length ? type : TYPES.DISABLED;
            }
        }

        return TYPES.HIDDEN;
    }

    return {
        startSession,
        abortSession,
        onApplePayClicked,
        getApplePaymentType,
        checkApplePayments,
        prepareSession,
        enableGuestCheckout,
        getLineItems,
        TYPES
    };
})();
